import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileDown, Inbox, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => (v === "" || v == null ? "–" : `${Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const de = (s) => (s && s.length >= 10 ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : s || "–");
const STATUS = { offen: "Offen", bezahlt: "Bezahlt", an_buchhaltung: "An Buchhaltung", abgelehnt: "Abgelehnt" };

const Detail = ({ e }) => (
  <tr data-testid={`eingang-detail-${e.id}`}>
    <td colSpan={7} className="bg-stone-50 px-4 py-3 text-sm">
      <div className="grid sm:grid-cols-3 gap-2 text-stone-700">
        <span>Format: {e.format}</span><span>USt-IdNr. Lieferant: {e.lieferant_ustid || "–"}</span><span>IBAN: {e.iban || "–"}</span>
        <span>Netto: {eur(e.netto)}</span><span>Steuer: {eur(e.steuer)}</span><span>Zahlbetrag: {eur(e.zahlbetrag)}</span>
      </div>
      <ul className="mt-2 divide-y divide-stone-200">
        {(e.positionen || []).map((p, i) => (
          <li key={i} className="py-1 flex justify-between gap-4"><span>{p.menge} × {p.beschreibung}</span><span className="tabular-nums">{eur(p.gesamt)}</span></li>
        ))}
      </ul>
    </td>
  </tr>
);

export default function Eingangsrechnungen() {
  const [liste, setListe] = useState([]);
  const [offen, setOffen] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const laden = useCallback(() => axios.get(`${API}/eingangsrechnungen`).then((r) => setListe(r.data)).catch(() => toast.error("Liste konnte nicht geladen werden")), []);
  useEffect(() => { laden(); }, [laden]);

  const hochladen = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await axios.post(`${API}/eingangsrechnungen`, fd);
        toast.success(`${data.lieferant}: Rechnung ${data.nummer} übernommen${data.dublette_von ? " (Achtung: schon einmal hochgeladen)" : ""}`);
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || "Upload fehlgeschlagen"}`);
      }
    }
    setBusy(false);
    if (input.current) input.current.value = "";
    laden();
  };
  const aendern = async (id, body) => {
    try { await axios.put(`${API}/eingangsrechnungen/${id}`, body); } catch (err) { toast.error(err.response?.data?.detail || "Änderung fehlgeschlagen"); }
    laden();
  };
  const setStatus = (id, status) => aendern(id, { status });
  const loeschen = (id) => { if (window.confirm("Eingangsrechnung entfernen?")) aendern(id, { loeschen: true }); };
  const datei = async (e) => {
    const res = await axios.get(`${API}/eingangsrechnungen/${e.id}/datei`, { responseType: "blob" }).catch(() => null);
    if (!res) return toast.error("Datei konnte nicht geladen werden");
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = e.dateiname; document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl" data-testid="eingangsrechnungen-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-stone-900">Eingangsrechnungen</h1>
          <p className="text-sm text-stone-600 mt-1">E-Rechnungen von Lieferanten hochladen (XRechnung als XML oder ZUGFeRD-PDF). Das CRM liest sie aus; die Buchhaltung läuft weiter über Agenda.</p>
        </div>
        <input ref={input} type="file" multiple accept=".xml,.pdf" className="hidden" onChange={(e) => hochladen([...e.target.files])} data-testid="eingang-file-input" />
        <Button onClick={() => input.current?.click()} disabled={busy} data-testid="eingang-upload-btn"><Upload size={16} className="mr-2" />{busy ? "Wird gelesen…" : "E-Rechnung hochladen"}</Button>
      </div>
      {liste.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 p-10 text-center text-stone-500" data-testid="eingang-leer"><Inbox className="mx-auto mb-2" />Noch keine Eingangsrechnungen.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
          <table className="w-full text-sm" data-testid="eingang-tabelle">
            <thead className="bg-stone-100 text-left text-xs uppercase tracking-wider text-stone-600">
              <tr><th className="px-3 py-2">Datum</th><th className="px-3 py-2">Lieferant</th><th className="px-3 py-2">Nr.</th><th className="px-3 py-2 text-right">Brutto</th><th className="px-3 py-2">Fällig</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {liste.map((e) => [
                <tr key={e.id} className="border-t border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => setOffen(offen === e.id ? null : e.id)} data-testid={`eingang-zeile-${e.id}`}>
                  <td className="px-3 py-2">{de(e.datum)}</td><td className="px-3 py-2 font-medium">{e.lieferant}</td><td className="px-3 py-2">{e.nummer}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{eur(e.brutto)}</td><td className="px-3 py-2">{de(e.faellig)}</td>
                  <td className="px-3 py-2" onClick={(ev) => ev.stopPropagation()}>
                    <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} className="rounded border border-stone-300 bg-white px-2 py-1 text-xs" data-testid={`eingang-status-${e.id}`}>
                      {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                    <button onClick={() => datei(e)} title="Original herunterladen" className="p-1 text-stone-600 hover:text-stone-900" data-testid={`eingang-datei-${e.id}`}><FileDown size={16} /></button>
                    <button onClick={() => loeschen(e.id)} title="Entfernen" className="p-1 text-stone-400 hover:text-red-600" data-testid={`eingang-loeschen-${e.id}`}><Trash2 size={16} /></button>
                  </td>
                </tr>,
                offen === e.id && <Detail key={`${e.id}-d`} e={e} />,
              ])}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
